import clsx from "clsx";
import { useLocation } from "react-router-dom";

import { Eye, Globe } from "lucide-react";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { userIsFPEmployee } from "@/util";

import {
  disablePreviewMode,
  getTabPreviewState,
  optTabIntoPreviewMode,
  optTabOutOfPreviewMode,
} from "./runtime";
import { pathHasPreviewData } from "./supported-features";

export function PreviewModeBar() {
  const { pathname } = useLocation();
  const accountMetadata = useAccountMetadata();

  const state = getTabPreviewState();
  if (state === "off") return null;
  const preview = state === "preview";
  // Routes without preview handlers show real data even in a preview tab.
  // The real-data bar ignores the account's banner setting: missing that a
  // tab is out of preview mid-demo is the costly mistake.
  if (
    preview &&
    (!pathHasPreviewData(pathname) ||
      accountMetadata?.config?.disableMasqueradeWarningBanner)
  ) {
    return null;
  }

  // The per-tab opt-out is an internal tool.
  const canSwitchTab = userIsFPEmployee();
  const buttonClassName = clsx(
    "h-5 shrink-0 cursor-pointer rounded-sm border border-current px-2 text-[11px] font-semibold tracking-[.02em] uppercase",
    preview
      ? "hover:bg-preview-strip-text/10"
      : "hover:bg-preview-strip-real-data-text/20",
  );

  return (
    <div
      role="region"
      aria-label="Preview mode"
      className={clsx(
        "flex h-7 w-full items-center gap-2.5 px-4 text-[13px] leading-4 font-medium",
        preview
          ? "bg-preview-strip text-preview-strip-text"
          : "bg-preview-strip-real-data text-preview-strip-real-data-text",
      )}
    >
      {preview ? (
        <Eye size={16} aria-hidden />
      ) : (
        <Globe size={16} aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate">
        {preview
          ? "PREVIEW: All data in this tab is synthetic."
          : "REAL DATA in this tab. Your other tabs are still in preview."}
      </span>
      {canSwitchTab && (
        <button
          className={buttonClassName}
          onClick={preview ? optTabOutOfPreviewMode : optTabIntoPreviewMode}
        >
          {preview ? "Use real data in this tab" : "Use preview in this tab"}
        </button>
      )}
      <button
        className={buttonClassName}
        onClick={disablePreviewMode}
        title="Exit preview in all tabs"
      >
        Exit
      </button>
    </div>
  );
}
